import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import {of as observableOf,  Observable } from 'rxjs';
import {catchError} from 'rxjs/operators';
import { ErrorResponse, SuccessResponse } from '@shared/services/api/response';
import { ActivityDetailFilter, ActivityFilter, IActivityLogs, IEvent, IEventDesign, IEventsForDropdown } from '@shared/services/api/dashboard/events-service/events-model';
import { IPublicUrls } from '../../legacy/timelines-service/timeline-model';
import { CalendarTranslationsResponse, ICloneEvent } from '@routes/events/models/event-model';
import { ITranslation } from '@shared/models/language.model';

@Injectable()
export class EventsService {
    constructor(
        private http: HttpClient
    ) { }

    private static get URL(): string { return `${environment.dashboardApi.baseUrl}${environment.dashboardApi.prefix}`; }
    private static readonly ENDPOINT = 'events/{eventId}';

    /** GET */
    public getEvent(eventId: string): Observable<SuccessResponse<IEvent> | ErrorResponse>  {
        return this.http
            .get<SuccessResponse<IEvent>>(
                EventsService.URL +
                EventsService.ENDPOINT.replace('{eventId}', eventId)
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public getPublicUrls(eventId: string, isFrontDesk: boolean): Observable<SuccessResponse<IPublicUrls> | ErrorResponse>  {
        return this.http
            .get<SuccessResponse<IPublicUrls>>(
                EventsService.URL +
                EventsService.ENDPOINT.replace('{eventId}', eventId) +
                '/publicurls' +
                '?isFrontDesk=' + isFrontDesk
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }


    /** PUT */
    public updateEvent(eventId: string, event: IEvent): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(
                EventsService.URL +
                EventsService.ENDPOINT.replace('{eventId}', eventId),
                event
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }
    public cloneEvent(model: ICloneEvent): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(
                EventsService.URL +
                EventsService.ENDPOINT.replace('{eventId}', model.eventId) +
                '/clones',
                model
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public updateDesign(data: IEventDesign): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(
                EventsService.URL +
                EventsService.ENDPOINT.replace('{eventId}', data.eventId) +
                '/designs',
                this.convertToFormData(data)
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public getCalendarTranslation(eventId: string, locationDateTimeId: string) {
        return this.http
                .get<SuccessResponse<CalendarTranslationsResponse>>(
                    EventsService.URL +
                    EventsService.ENDPOINT.replace('{eventId}', eventId) +
                    '/locationdatetimes/' +
                    locationDateTimeId +
                    '/calendartranslations'
                ).pipe(
                catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public updateCalendarTranslation(eventId: string, locationDateTimeId: string, calendarTranslations: ITranslation[]): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(
                EventsService.URL +
                EventsService.ENDPOINT.replace('{eventId}', eventId) +
                '/locationdatetimes/' +
                locationDateTimeId +
                '/calendartranslations' ,
                { calendarTranslations }
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    /** POST */
    public addEvent(event: IEvent): Observable<SuccessResponse<boolean> | ErrorResponse>  {
        return this.http
            .post<SuccessResponse<boolean>>(
                EventsService.URL +
                'events',
                event
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }


    /** DELETE */
    public deleteEvent(eventId: string): Observable<SuccessResponse<boolean> | ErrorResponse>  {
        return this.http
            .delete<SuccessResponse<boolean>>(
                EventsService.URL +
                EventsService.ENDPOINT.replace('{eventId}', eventId)
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public softDeleteEvent(eventId: string): Observable<SuccessResponse<boolean> | ErrorResponse>  {
        return this.http
            .delete<SuccessResponse<boolean>>(
                EventsService.URL +
                EventsService.ENDPOINT.replace('{eventId}', eventId) +
                '/' + 'softdeletes',
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public getActivityLogs(activityFilter: ActivityFilter): Observable<SuccessResponse<IActivityLogs> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<IActivityLogs>>(
                EventsService.URL + 'events/activities',
                activityFilter)
            .pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getActivityLogsByEvent(activityFilter: ActivityDetailFilter, eventId: string): Observable<SuccessResponse<IActivityLogs> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<IActivityLogs>>(
                EventsService.URL +
                EventsService.ENDPOINT.replace('{eventId}', eventId) +
                '/activities', activityFilter)
            .pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public getEventsForDropdown(): Observable<SuccessResponse<IEvent> | ErrorResponse>  {
        return this.http
            .get<SuccessResponse<IEventsForDropdown>>(
                EventsService.URL + 'events/dropdowns'
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    private convertToFormData(data: IEventDesign): FormData {
        const formData: FormData = new FormData();

        // Remove empty files
        const banners = [];
        data.bannerContents?.forEach(content => {
            if (!content.isRemoved && (content.file || content.id)) {
                banners.push(content);
            }
        });
        data.bannerContents = [...banners];

        // Top level
        formData.append('advancedTemplateId', data.advancedTemplateId);
        formData.append('eventId', data.eventId);

        // Banner
        data.bannerContents.forEach((fileData, index) => {
            formData.append(`bannerContents[${index}].id`, fileData.id ?? '');
            formData.append(`bannerContents[${index}].canClone`, JSON.stringify(fileData.canClone));
            formData.append(`bannerContents[${index}].languageId`, fileData.languageId);
            formData.append(`bannerContents[${index}].contentMediaType`, JSON.stringify(fileData.contentMediaType));
            formData.append(`bannerContents[${index}].contentContextType`, JSON.stringify(fileData.contentContextType));
            formData.append(`bannerContents[${index}].file`, fileData.file);
        });

        // Footers
        data.footers.forEach((footer, index) => {
            formData.append(`footers[${index}].index`, JSON.stringify(footer.index));
            formData.append(`footers[${index}].footerId`, footer.footerId);
            formData.append(`footers[${index}].contentContextType`, JSON.stringify(footer.contentContextType));
        });

        return formData;
    }
}
